import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flame, Truck, Briefcase, Heart, Users, ChevronRight, MapPin, Calendar as CalIcon, Utensils, Wind } from "lucide-react";

import { fetchPublicEvents, fetchMenuItems } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import EmberButton from "@/components/EmberButton";
import SectionHeader from "@/components/SectionHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSettings } from "@/lib/settings";

function Stat({ k, v }) {
    return (
        <div className="surface-card rounded-xl p-4">
            <div className="font-display text-3xl text-orange-300 leading-none">{k}</div>
            <div className="text-xs uppercase tracking-[0.24em] text-white/60 mt-1">{v}</div>
        </div>
    );
}

function EventBanner() {
    return (
        <div className="h-40 relative overflow-hidden bg-[#0c0c0d] flex items-center justify-center select-none">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-20 bg-orange-500/20 blur-3xl rounded-full" />
            <Wind size={26} className="absolute top-3 left-8 text-white/[0.07] rotate-12" />
            <Wind size={18} className="absolute top-7 left-20 text-white/[0.05] -rotate-6" />
            <Wind size={22} className="absolute top-4 right-20 text-white/[0.06] rotate-6" />
            <Flame size={64} className="absolute bottom-2 left-10 text-orange-500/20" />
            <Flame size={88} className="absolute bottom-1 left-1/2 -translate-x-1/2 text-orange-400/15" />
            <Flame size={50} className="absolute bottom-3 right-12 text-orange-500/18" />
            <Utensils size={30} className="absolute top-4 right-7 text-white/[0.07] rotate-12" />
            <div className="relative z-10 text-center">
                <div className="text-[9px] uppercase tracking-[0.48em] text-orange-300/40 font-display">Smokin Mo's BBQ</div>
            </div>
        </div>
    );
}

function ServiceCard({ icon: Icon, title, desc }) {
    return (
        <div className="surface-card rounded-xl p-5 transition-shadow" data-testid={`service-card-${title.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-[rgba(255,140,0,0.12)] border border-[rgba(255,140,0,0.35)] text-orange-300">
                    <Icon size={20} />
                </div>
                <div className="font-display text-xl tracking-[0.05em] text-white">{title}</div>
            </div>
            <p className="mt-3 text-sm text-white/75 leading-relaxed">{desc}</p>
        </div>
    );
}

export default function HomePage() {
    const { settings } = useSettings();
    const [events, setEvents] = useState([]);
    const [specials, setSpecials] = useState([]);

    useEffect(() => {
        fetchPublicEvents({ upcoming_only: true }).then((d) => setEvents(d.slice(0, 4))).catch(() => {});
        fetchMenuItems({ category: "specials" }).then((d) => setSpecials(d.slice(0, 4))).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="home-hero">
                <div className="heat-band absolute inset-x-0 top-0 h-24 pointer-events-none" />
                <div className="container-narrow pt-14 pb-20 md:pt-24 md:pb-32">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.6 }}
                        className="flex flex-col items-start gap-6"
                    >
                        <div className="flex items-center gap-3">
                            <BrandLogo size={64} />
                            <Badge variant="outline" className="text-orange-300 border-orange-400/40 bg-orange-400/10" data-testid="hero-tagline-badge">
                                {settings?.tagline || "All Meats Wood Smoked On Site Every Day"}
                            </Badge>
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-[0.04em] text-white leading-[0.95] text-ember-glow" data-testid="hero-title">
                            Georgia smoke.<br/>
                            <span className="text-orange-300">Jersey grit.</span>
                        </h1>
                        <p className="max-w-2xl text-white/80 text-lg leading-relaxed" data-testid="hero-subtitle">
                            Slow-cooked Southern barbecue smoked 14–16 hours over Cherry, Hickory, Red Oak and Apple wood — from Pitmaster Mo, a Wharton, NJ native who spent decades mastering the craft in Georgia before bringing it back home. Mobile catering, food truck pop-ups, weddings, corporate, and everything in between.
                        </p>
                        <div className="flex flex-wrap gap-3 mt-2">
                            <EmberButton to="/contact" data-testid="home-hero-book-catering-button">
                                Book Catering
                            </EmberButton>
                            <EmberButton to="/menu" variant="ghost" data-testid="home-hero-view-menu-button">
                                View Menu
                            </EmberButton>
                        </div>
                        <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-8 max-w-md">
                            <Stat k="2011" v="Est. Wharton, NJ" />
                            <Stat k="4 Woods" v="Cherry · Hickory · Oak · Apple" />
                            <Stat k="14-16hr" v="Smoke Times" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* MO'S STORY TEASER */}
            <section className="section" data-testid="home-story-section">
                <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7">
                        <SectionHeader
                            eyebrow="Mo's Story"
                            title="From Wharton to Georgia. Back home with fire."
                            subtitle="A Wharton, NJ native who moved to Georgia in '92 and spent over 30 years learning real Southern BBQ — Mo came home in 2024 with a reputation, an Ole Hickory pit, and a Best Food Truck Atlanta 2015 title to back it up."
                        />
                        <p className="text-white/80 leading-relaxed">
                            Every brisket is rubbed by hand and smoked 14 to 16 hours over Cherry, Hickory, Red Oak, and Apple wood. Every sauce is mixed in-house. Every plate leaves the truck the way Mo would feed his own family.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <EmberButton to="/about" data-testid="home-read-story-button">Read the Story</EmberButton>
                            <Link to="/gallery" className="text-orange-300 hover:text-orange-200 inline-flex items-center gap-1 text-sm font-medium" data-testid="home-see-gallery-link">
                                See the gallery <ChevronRight size={16} />
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                        <Stat k="Ole Hickory" v="Pit-cooked" />
                        <Stat k="Hand-Rubbed" v="Every Cut" />
                        <Stat k="Best Food Truck" v="Atlanta 2015" />
                        <Stat k="Mobile-Ready" v="Anywhere in NJ" />
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="section" data-testid="home-services-section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="Services"
                        title="What Mo cooks for."
                        subtitle="Whatever the crowd looks like, Mo brings the smoke to it."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <ServiceCard icon={Truck} title="Mobile Catering" desc="The truck rolls up loaded with meats smoked fresh the night before. Backyards, parking lots, fields — we go where you are." />
                        <ServiceCard icon={Heart} title="Weddings" desc="Plated or buffet. Curated menus, on-site service, lasting impressions." />
                        <ServiceCard icon={Briefcase} title="Corporate" desc="Office lunches, employee appreciation, holiday parties. Lead times that work." />
                        <ServiceCard icon={Users} title="Private Events" desc="Birthdays, graduations, family reunions. Real food, real fire, no shortcuts." />
                    </div>
                </div>
            </section>

            {/* RECENT EVENTS */}
            <section className="section" data-testid="home-recent-events-section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="On the Road"
                        title="Recent & upcoming events."
                        subtitle="Where to find Mo next."
                    />
                    {events.length === 0 ? (
                        <Card className="surface-card border-0">
                            <CardContent className="p-6 text-white/70">No upcoming events yet — check back soon, or book us for yours.</CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {events.map((e) => (
                                <Card key={e.id} className="surface-card border-0 overflow-hidden" data-testid="home-event-card">
                                    <EventBanner />
                                    <CardContent className="p-5">
                                        <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90 flex items-center gap-1">
                                            <CalIcon size={12} /> {new Date(e.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                        </div>
                                        <div className="font-display text-xl mt-1 text-white">{e.title}</div>
                                        {e.location ? (
                                            <div className="flex items-center gap-1 text-white/70 text-sm mt-1">
                                                <MapPin size={14} className="text-orange-300" /> {e.location}
                                            </div>
                                        ) : null}
                                        {e.start_time ? (
                                            <div className="text-white/60 text-sm mt-1">{e.start_time}{e.end_time ? ` – ${e.end_time}` : ''}</div>
                                        ) : null}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                    <div className="mt-8">
                        <EmberButton to="/events" variant="ghost" data-testid="home-see-all-events-button">See full calendar</EmberButton>
                    </div>
                </div>
            </section>

            {/* SPECIALS PEEK */}
            {specials.length > 0 && (
                <section className="section" data-testid="home-specials-section">
                    <div className="container-narrow">
                        <SectionHeader
                            eyebrow="Daily Specials"
                            title="What's hitting the fryer this week."
                            subtitle="A taste of what's rotating on the menu."
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {specials.map((s) => (
                                <Card key={s.id} className="surface-card border-0" data-testid="home-special-card">
                                    <CardContent className="p-5">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="font-display text-lg tracking-[0.04em] text-white pr-3">{s.name}</div>
                                            <div className="text-orange-300 font-display text-lg whitespace-nowrap">{s.price}</div>
                                        </div>
                                        {s.description && <p className="text-white/70 text-sm mt-2 leading-relaxed line-clamp-3">{s.description}</p>}
                                        <div className="mt-3">
                                            <Badge className="bg-orange-400/15 text-orange-300 border-orange-400/30" variant="outline">
                                                <Flame size={12} className="mr-1" /> Special
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                        <div className="mt-8">
                            <EmberButton to="/menu" data-testid="home-explore-menu-button">Explore Full Menu</EmberButton>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Band */}
            <section className="section section-no-backdrop" data-testid="home-cta-section">
                <div className="container-narrow">
                    <div className="surface-card rounded-2xl p-8 sm:p-12 relative overflow-hidden">
                        <div className="heat-band absolute inset-x-0 top-0 h-12" />
                        <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-6">
                            <div>
                                <div className="text-[11px] tracking-[0.36em] uppercase text-orange-300/90 mb-2">Catering inquiries open</div>
                                <h3 className="font-display text-3xl sm:text-4xl tracking-[0.05em] text-white">Feed the crowd.<br/>Mo handles the fire.</h3>
                            </div>
                            <EmberButton to="/contact" data-testid="home-cta-book-button">Start a Booking</EmberButton>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
